import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Smartphone, Layers, Cpu, ShieldCheck, 
  ArrowRight, Sparkles, Bell, Wifi, Rocket, Calendar,
  Building2, ShoppingBag, Video, Globe
} from "lucide-react";
import { 
  SiSwift, SiKotlin, SiAndroid, SiReact, SiFlutter, 
  SiFirebase, SiNodedotjs, SiSqlite, SiGraphql 
} from "react-icons/si";

import AppDevelopment from "../../plans/AppDevelopment";

export default function AppDevelopmentService() {
  const appDevTypes = [
    {
      title: "Cross-Platform & Hybrid App Development",
      icon: <Layers className="text-blue-600 dark:text-cyan-400" size={28} />,
      overview: "Highly cost-effective and rapid development approach utilizing a single codebase to deploy high-performance applications simultaneously across both iOS and Android platforms, ideal for startups and global enterprises seeking faster time-to-market.",
      coreTechLabel: "Core Tech Stack",
      techs: ["Flutter", "React Native", "Expo", "JavaScript", "TypeScript", "Tailwind CSS"],
      marketFit: "High demand across Indian D2C brands, fintech startups, and international SMBs looking to optimize development budgets."
    },
    {
      title: "Native Mobile App Development",
      icon: <Smartphone className="text-indigo-600 dark:text-violet-400" size={28} />,
      overview: "High-performance, platform-specific engineering tailored strictly to device hardware guidelines, ensuring butter-smooth animations, complex processing, and deep integration with native device features (Bluetooth, NFC, Apple Pay/Google Pay).",
      coreTechLabel: "Core Tech Stack",
      techs: ["iOS: Swift, Objective-C, Xcode", "Android: Kotlin, Java, Android Studio"],
      marketFit: "Premium global clients, gaming studios, high-security banking, and hardware-integrated IoT applications."
    },
    {
      title: "Progressive Web Applications (PWAs)",
      icon: <Globe className="text-cyan-600 dark:text-cyan-300" size={28} />,
      overview: "Hybrid web-to-mobile solutions that run directly inside modern web browsers while offering native app-like capabilities including offline mode, push notifications, and home-screen installation without App Store friction.",
      coreTechLabel: "Core Tech Stack",
      techs: ["Next.js", "React", "Service Workers", "Web App Manifests"],
      marketFit: "Emerging markets (Tier-2/3 Indian audience with limited data or storage) and international media/publishing platforms."
    },
    {
      title: "Enterprise Mobility & Workforce Apps",
      icon: <Building2 className="text-amber-500 dark:text-amber-400" size={28} />,
      overview: "Secure internal automation ecosystems designed to streamline field operations, employee collaboration, supply chains, and administrative tracking.",
      coreTechLabel: "Core Tech Stack",
      techs: ["React Native", "Node.js Backend APIs", "MySQL / PostgreSQL", "RBAC Architectures"],
      marketFit: "Large-scale manufacturing plants, logistics networks, enterprise corporations, and B2B operations in India and overseas."
    },
    {
      title: "On-Demand Service & Marketplace Apps",
      icon: <ShoppingBag className="text-emerald-600 dark:text-emerald-400" size={28} />,
      overview: "Multi-sided platforms connecting end-users, service providers, and administrators with real-time tracking, live chat, secure digital wallets, and automated booking workflows.",
      coreTechLabel: "Core Tech Stack",
      techs: ["MERN Stack (MongoDB, Express, React, Node)", "Socket.io Realtime", "Firebase", "Razorpay / Stripe / PayPal"],
      marketFit: "Hyperlocal delivery services, ride-hailing networks, wellness booking platforms, and global service aggregators."
    },
    {
      title: "OTT & Media Streaming Apps",
      icon: <Video className="text-rose-600 dark:text-rose-400" size={28} />,
      overview: "High-bandwidth digital video and audio streaming solutions supporting adaptive bitrate playback, secure digital rights management (DRM), subscription models, and personalized content recommendation engines.",
      coreTechLabel: "Core Tech Stack",
      techs: ["React / Next.js Frontend", "Node.js Streaming Servers", "AWS S3 / CloudFront CDN", "MySQL"],
      marketFit: "Entertainment startups, digital creators, educational institutions, and media brands targeting global streaming audiences."
    }
  ];

  const capabilities = [
    {
      icon: <Smartphone className="text-blue-600 dark:text-cyan-400" size={26} />,
      title: "Native iOS Apps (Swift / SwiftUI)",
      desc: "High-performance iOS mobile applications engineered using Apple's latest Swift & SwiftUI frameworks, designed specifically for iPhones and iPads.",
      highlights: ["Swift & SwiftUI", "iOS 18 SDK", "Human Interface UX"]
    },
    {
      icon: <Layers className="text-indigo-600 dark:text-violet-400" size={26} />,
      title: "Native Android Apps (Kotlin / Jetpack)",
      desc: "Robust, scalable Android applications built using Kotlin & Jetpack Compose, fully compliant with Material 3 design guidelines.",
      highlights: ["Kotlin & Jetpack", "Material 3 Design", "Android 15 Architecture"]
    },
    {
      icon: <Cpu className="text-cyan-600 dark:text-cyan-300" size={26} />,
      title: "Cross-Platform Apps (React Native / Flutter)",
      desc: "Dual-store mobile solutions sharing a single unified codebase for iOS and Android, cutting time-to-market and development costs.",
      highlights: ["React Native / Flutter", "Single Codebase", "60 FPS Performance"]
    },
    {
      icon: <Bell className="text-amber-500 dark:text-amber-400" size={26} />,
      title: "Push Notifications & Real-Time Sync",
      desc: "Integrating Firebase Cloud Messaging (FCM), APNs, background sync, and real-time WebSocket communication.",
      highlights: ["Firebase FCM & APNs", "WebSockets Sync", "Background Tasks"]
    },
    {
      icon: <Wifi className="text-emerald-600 dark:text-emerald-400" size={26} />,
      title: "Offline-First Mobile Architecture",
      desc: "Local SQLite, WatermelonDB, or CoreData sync ensuring uninterrupted user experiences even in low connectivity environments.",
      highlights: ["SQLite & CoreData", "Offline Caching", "Auto-Sync Engine"]
    },
    {
      icon: <ShieldCheck className="text-rose-600 dark:text-rose-400" size={26} />,
      title: "App Store Publishing & Compliance",
      desc: "End-to-end Apple App Store Review & Google Play Console submission, managing certificates, provisioning profiles, and privacy declarations.",
      highlights: ["Apple App Store", "Google Play Store", "100% Approval SLA"]
    }
  ];

  const mobileTechStackData = [
    {
      name: "Swift",
      category: "iOS Native Language",
      icon: React.createElement(SiSwift as any, { className: "text-[#F05138]", size: 32 })
    },
    {
      name: "SwiftUI",
      category: "Apple Declarative UI",
      icon: React.createElement(SiSwift as any, { className: "text-[#007AFF]", size: 32 })
    },
    {
      name: "Kotlin",
      category: "Android Native Language",
      icon: React.createElement(SiKotlin as any, { className: "text-[#7F52FF]", size: 32 })
    },
    {
      name: "Jetpack",
      category: "Android Architecture",
      icon: React.createElement(SiAndroid as any, { className: "text-[#3DDC84]", size: 32 })
    },
    {
      name: "React Native",
      category: "Cross-Platform Framework",
      icon: React.createElement(SiReact as any, { className: "text-[#61DAFB]", size: 32 })
    },
    {
      name: "Flutter",
      category: "Google Mobile SDK",
      icon: React.createElement(SiFlutter as any, { className: "text-[#02569B]", size: 32 })
    },
    {
      name: "Firebase",
      category: "Realtime & Push Auth",
      icon: React.createElement(SiFirebase as any, { className: "text-[#FFCA28]", size: 32 })
    },
    {
      name: "Node.js",
      category: "Mobile Backend API",
      icon: React.createElement(SiNodedotjs as any, { className: "text-[#5FA04E]", size: 32 })
    },
    {
      name: "SQLite",
      category: "Local Offline Storage",
      icon: React.createElement(SiSqlite as any, { className: "text-[#003B57]", size: 32 })
    },
    {
      name: "GraphQL",
      category: "Flexible API Engine",
      icon: React.createElement(SiGraphql as any, { className: "text-[#E10098]", size: 32 })
    }
  ];

  const workflow = [
    { step: "01", title: "Product Blueprint", desc: "User story mapping, feature prioritization, target audience analysis, and API specs." },
    { step: "02", title: "Mobile UI/UX Prototyping", desc: "Crafting interactive Figma mobile wireframes and human interface design patterns." },
    { step: "03", title: "Core App Development", desc: "Sprint-based mobile engineering with weekly test builds delivered via TestFlight & APKs." },
    { step: "04", title: "Store Launch & Maintenance", desc: "App Store & Play Store publishing with 24/7 server monitoring and feature updates." }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* HERO */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Mobile Innovation
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
            Next-Gen <span className="text-blue-600 dark:text-cyan-400">Mobile App Development</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            We engineer high-performance native iOS, Android, and cross-platform mobile apps. Built for speed, offline reliability, and seamless store approval.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link 
              to="/apply/app?plan=iOS%20Native%20App" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white shadow-lg shadow-blue-600/25 transition-all cursor-pointer"
            >
              <Rocket size={18} className="!text-white" />
              <span className="!text-white font-bold">Apply for App Plan</span>
              <ArrowRight size={16} className="!text-white" />
            </Link>

            <Link 
              to="/Meeting" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-blue-500 transition-all cursor-pointer shadow-sm"
            >
              <Calendar size={18} />
              <span>Book App Consultation</span>
            </Link>
          </div>
        </div>

        {/* TYPES OF APP DEVELOPMENT SERVICES SECTION */}
        <div className="space-y-8">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3.5 py-1.5 rounded-full border border-blue-200 dark:border-blue-500/20">
              Specialized Solutions
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Types of Mobile App Development Services
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">
              Custom iOS & Android mobile engineering tailored for startups, enterprises, and global marketplaces.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {appDevTypes.map((type, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className="group relative overflow-hidden backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border border-slate-200/90 dark:border-slate-800/90 rounded-3xl p-7 space-y-6 shadow-xl hover:shadow-2xl hover:border-blue-500/50 dark:hover:border-cyan-500/50 transition-all flex flex-col justify-between"
              >
                {/* BLURRY BACKDROP GLOW DECORATION */}
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-500/10 dark:bg-cyan-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform pointer-events-none" />

                <div className="space-y-4 relative z-10">
                  {/* ANIMATED FLOATING ICON */}
                  <div className="flex items-center gap-3.5">
                    <motion.div 
                      animate={{ scale: [1, 1.06, 1] }}
                      transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: idx * 0.2 }}
                      className="w-13 h-13 p-3 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-md shrink-0 group-hover:scale-110 transition-transform"
                    >
                      {type.icon}
                    </motion.div>
                    <h3 className="text-lg font-extrabold text-slate-900 dark:text-white leading-snug group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                      {type.title}
                    </h3>
                  </div>

                  <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed font-medium">
                    {type.overview}
                  </p>
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800/80 relative z-10">
                  {/* CORE TECH STACK */}
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-1.5">
                      ⚡ {type.coreTechLabel}:
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {type.techs.map((tech, tIdx) => (
                        <span key={tIdx} className="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-cyan-300 text-[11px] font-extrabold border border-blue-200/80 dark:border-cyan-500/30">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* MARKET FIT BANNER */}
                  <div className="p-3 rounded-2xl bg-slate-100/90 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 space-y-1">
                    <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                      🎯 Market Fit:
                    </span>
                    <p className="text-[11px] text-slate-600 dark:text-slate-300 font-medium leading-normal">
                      {type.marketFit}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* VISUAL MOBILE TECH STACK GRID */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Mobile Tech Ecosystem
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Mobile Technology Stack & Engines
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              Native languages, cross-platform engines, and cloud backends engineered for app store dominance.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {mobileTechStackData.map((tech, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.04, y: -2 }}
                className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 p-4 rounded-2xl flex flex-col items-center justify-center text-center space-y-2.5 transition-all shadow-sm hover:shadow-md hover:border-blue-500/40 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm">
                  {tech.icon}
                </div>
                <div>
                  <h4 className="text-sm font-extrabold text-slate-900 dark:text-white">{tech.name}</h4>
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 block mt-0.5">{tech.category}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CAPABILITIES GRID WITH ANIMATED LOGOS & ICONS */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Mobile Capabilities
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Mobile App Development Capabilities
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              Comprehensive iOS & Android mobile engineering tailored for app store success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-7 space-y-5 shadow-lg hover:shadow-2xl hover:border-blue-500/50 dark:hover:border-cyan-500/50 transition-all flex flex-col justify-between cursor-pointer"
              >
                <div className="space-y-4">
                  {/* ANIMATED FLOATING ICON BADGE */}
                  <motion.div 
                    animate={{ y: [0, -4, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "easeInOut", delay: idx * 0.2 }}
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/60 dark:to-cyan-950/60 border border-blue-200/80 dark:border-cyan-500/30 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform"
                  >
                    {cap.icon}
                  </motion.div>

                  <div className="space-y-2">
                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                      {cap.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed font-medium">
                      {cap.desc}
                    </p>
                  </div>

                  {/* HIGHLIGHT FEATURE CHIPS */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {cap.highlights.map((item, hIdx) => (
                      <span key={hIdx} className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800/80 text-[11px] font-bold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/80">
                        📱 {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80">
                  <Link to="/enquirynow" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:text-blue-700 dark:hover:text-cyan-300 group/link">
                    <span>Get App Quote</span>
                    <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* WORKFLOW METHODOLOGY */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-cyan-400">
              Mobile Lifecycle
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
              From Prototype to App Store Launch
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflow.map((item, idx) => (
              <div key={idx} className="bg-slate-50 dark:bg-slate-800/60 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/60 space-y-3">
                <span className="text-2xl font-black text-blue-600 dark:text-cyan-400">{item.step}</span>
                <h4 className="text-base font-extrabold text-slate-900 dark:text-white">{item.title}</h4>
                <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* APP DEVELOPMENT PRICING SLIDES COMPONENT FROM PRICING PAGE */}
      <div className="w-full my-8">
        <AppDevelopment />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* CTA BANNER */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-900/90 dark:to-indigo-950/90 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready to Build Your iOS & Android App?
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto text-sm font-medium">
            Schedule a 1-on-1 Mobile Discovery Session with our mobile software lead. We will review your wireframes or idea and provide a complete milestone roadmap.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link 
              to="/apply/app?plan=iOS%20Native%20App" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-white text-blue-700 hover:bg-slate-100 shadow-md flex items-center gap-2 cursor-pointer"
            >
              <span>Apply for App Plan</span>
              <ArrowRight size={16} />
            </Link>
            <Link 
              to="/Pricing" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-blue-800/80 hover:bg-blue-800 text-white border border-blue-400/30 flex items-center gap-2 cursor-pointer"
            >
              <span>View All Pricing Plans</span>
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
